import React, { useEffect, useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { HandshakeIcon, RocketIcon, TrendingUpIcon, XIcon } from 'lucide-react';
import { SEO } from '../components/SEO';
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 100,
      damping: 20
    }
  }
};
export function PartnerPage() {
  const [comingSoonOpen, setComingSoonOpen] = useState(false);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    if (comingSoonOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [comingSoonOpen]);
  return <div className="min-h-screen bg-white" data-id="element-433">
      <SEO title="Partner with 4ALL AI — Advertise Your AI Tool to Thousands" description="Reach engaged users actively searching for AI tools. Partner with 4ALL AI to list your AI product, invest in AI accessibility, or collaborate on AI education. AI companies and investors welcome." keywords="advertise AI tool, AI tool listing, AI tool directory advertising, AI partnership, AI investor, promote AI product, AI tool marketing, AI tool visibility, AI company advertising, list AI tool, AI tool promotion, AI advertising platform, AI tool marketplace" canonicalPath="/partner" data-id="element-434" />
      <Header data-id="element-435" />

      <main className="w-full" data-id="element-436">
        <motion.section className="max-w-3xl mx-auto px-6 pt-24 pb-16 text-center" variants={containerVariants} initial="hidden" animate="visible" data-id="element-437">
          {/* Coming Soon Badge */}
          <motion.div variants={itemVariants} className="mb-8" data-id="element-438">
            <span className="inline-block bg-grey-100 text-grey-600 text-sm font-semibold uppercase tracking-wider px-5 py-2 rounded-full" data-id="element-439">
              Coming Soon
            </span>
          </motion.div>

          {/* Headline */}
          <motion.h1 variants={itemVariants} className="text-5xl md:text-7xl font-bold text-black mb-6 tracking-tight" data-id="element-440">
            Partner with{' '}
            <span className="bg-gradient-to-r from-cyan-200 via-purple-200 to-pink-200 bg-clip-text text-transparent" data-id="element-441">
              4ALL
            </span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p variants={itemVariants} className="text-xl md:text-2xl text-grey-500 leading-relaxed max-w-2xl mx-auto mb-12" data-id="element-442">
            Are you providing AI services? Are you an investor? Find out more
            about partner opportunities with 4ALL.
          </motion.p>

          {/* Description */}
          <motion.p variants={itemVariants} className="text-lg text-grey-400 max-w-xl mx-auto mb-16" data-id="element-443">
            We are building something meaningful — and we are looking for
            like-minded partners who share our vision of making AI accessible to
            everyone. Our partner programme is launching soon.
          </motion.p>

          {/* Cards */}
          <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16" data-id="element-444">
            <div className="bg-grey-100 rounded-2xl p-8 text-center" data-id="element-445">
              <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-446">
                <RocketIcon className="w-6 h-6 text-white" data-id="element-447" />
              </div>
              <h3 className="font-bold text-black text-lg mb-2" data-id="element-448">
                AI Service Providers
              </h3>
              <p className="text-grey-500 text-sm leading-relaxed" data-id="element-449">
                Get your tools in front of the right audience — people who are
                actively looking for solutions.
              </p>
            </div>
            <div className="bg-grey-100 rounded-2xl p-8 text-center" data-id="element-450">
              <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-451">
                <TrendingUpIcon className="w-6 h-6 text-white" data-id="element-452" />
              </div>
              <h3 className="font-bold text-black text-lg mb-2" data-id="element-453">Investors</h3>
              <p className="text-grey-500 text-sm leading-relaxed" data-id="element-454">
                Join us in democratising AI access. We are building the go-to
                platform for everyday AI discovery.
              </p>
            </div>
            <div className="bg-grey-100 rounded-2xl p-8 text-center" data-id="element-455">
              <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-456">
                <HandshakeIcon className="w-6 h-6 text-white" data-id="element-457" />
              </div>
              <h3 className="font-bold text-black text-lg mb-2" data-id="element-458">
                Collaborators
              </h3>
              <p className="text-grey-500 text-sm leading-relaxed" data-id="element-459">
                Whether you are a creator, educator, or community builder — let
                us explore how we can work together.
              </p>
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div variants={itemVariants} data-id="element-460">
            <button onClick={() => setComingSoonOpen(true)} className="inline-block bg-black text-white px-10 py-5 rounded-full text-lg font-bold hover:bg-grey-800 transition-colors shadow-lg" data-id="element-461">
              Get Started
            </button>
            <p className="text-grey-400 text-sm mt-4" data-id="element-462">
              Drop us a line and we will be in touch when we launch.
            </p>
          </motion.div>
        </motion.section>
      </main>

      {/* Coming Soon Lightbox */}
      <AnimatePresence data-id="element-463">
        {comingSoonOpen && <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" data-id="element-464">
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2
        }} className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setComingSoonOpen(false)} data-id="element-465" />
            <motion.div initial={{
          opacity: 0,
          scale: 0.9,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.9,
          y: 20
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 25
        }} className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md z-10 p-8 sm:p-10 text-center" onClick={e => e.stopPropagation()} data-id="element-466">
              <button onClick={() => setComingSoonOpen(false)} className="absolute top-4 right-4 p-2 rounded-full hover:bg-grey-100 transition-colors" aria-label="Close" data-id="element-467">
                <XIcon className="w-5 h-5 text-grey-500" data-id="element-468" />
              </button>

              <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-black" data-id="element-469">
                <HandshakeIcon className="h-6 w-6 text-white" data-id="element-470" />
              </div>

              <h2 className="text-2xl font-bold text-black mb-2" data-id="element-471">
                Coming Soon
              </h2>
              <p className="text-grey-500 text-sm leading-relaxed mb-6" data-id="element-472">
                Our partner programme is launching soon. In the meantime, reach
                out to us directly and we'll be in touch when we launch.
              </p>

              <div className="space-y-3" data-id="element-473">
                <a href="mailto:partners@4all.ai?subject=Partnership%20Enquiry" className="block w-full rounded-full bg-black text-white px-6 py-3 text-sm font-semibold hover:bg-grey-800 transition-colors" data-id="element-474">
                  Email us about partnering
                </a>
                <button onClick={() => setComingSoonOpen(false)} className="block w-full rounded-full border border-grey-300 text-grey-700 px-6 py-3 text-sm font-medium hover:border-black hover:text-black transition-colors" data-id="element-475">
                  Close
                </button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Footer */}
      <footer className="border-t border-grey-200 mt-12 sm:mt-24" data-id="element-476">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8" data-id="element-477">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-grey-500" data-id="element-478">
            <p className="text-center sm:text-left" data-id="element-479">
              © 2026 4ALL AI | All Rights Reserved by Skeuomorphic xyz
            </p>
            <div className="flex items-center gap-6" data-id="element-480">
              <Link to="/privacy" className="hover:text-black transition-colors" data-id="element-481">
                Privacy
              </Link>
              <Link to="/terms" className="hover:text-black transition-colors" data-id="element-482">
                Terms
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>;
}